"use client"

/* eslint-disable */
import { useState, useEffect } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Box,
} from "@mui/material"
import { useSelector, useDispatch, shallowEqual } from "react-redux"
import { formatTime } from "../common/util/formatter"
import ReportFilter from "./components/ReportFilter"
import { prefixString } from "../common/util/stringUtils"
import { useTranslation } from "../common/components/LocalizationProvider"
import PageLayout from "../common/components/PageLayout"
import ReportsMenu from "./components/ReportsMenu"
import usePersistedState from "../common/util/usePersistedState"
import ColumnSelect from "./components/ColumnSelect"
import { useCatch } from "../reactHelper"
import useReportStyles from "./common/useReportStyles"
import TableShimmer from "../common/components/TableShimmer"
import { utils, writeFileXLSX } from "xlsx"
import { nativePostMessage } from "../common/components/NativeInterface"
import { sessionActions, errorsActions } from "../store"
import fetchOrThrow from "../common/util/fetchOrThrow"
import PrintHeader from "./common/PrintHeader"
import "./common/printStyles.css"

const EMPTY_OBJECT = Object.freeze({})
const FILTER_FORM_CONTROL_SX = {
  minWidth: 240,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    "& fieldset": { borderColor: "divider" },
    "&:hover fieldset": { borderColor: "primary.main" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
}
const FILTER_SELECT_SX = { backgroundColor: "background.paper" }

const columnsArray = [
  ["userId", "titleUser"],
  ["eventTime", "positionServerTime"],
  ["deviceId", "sharedDevice"],
  ["attributes", "commandData"],
]
const columnsMap = new Map(columnsArray)

// User filter component similar to actions-report-page
const UserFilter = ({ onChange, users = [], loading = false }) => {
  const [userId, setUserId] = useState("")
  const t = useTranslation()
  const usersMap = useSelector(
    (state) => state.usersMap ?? EMPTY_OBJECT,
    shallowEqual,
  )

  const handleChange = (e) => {
    setUserId(e.target.value)
    onChange(e.target.value)
  }

  const getUserDisplayName = (user) => {
    const nameFromMap = usersMap[user.id]
    if (nameFromMap) return nameFromMap
    if (user.name) return user.name
    return `${user.id}`
  }

  return (
    <FormControl
      fullWidth
      size="small"
      variant="outlined"
      sx={FILTER_FORM_CONTROL_SX}
    >
      <InputLabel id="user-select-label">{t("shared.user") || "User"}</InputLabel>
      <Select
        size="small"
        sx={FILTER_SELECT_SX}
        labelId="user-select-label"
        value={userId}
        onChange={handleChange}
        label={t("shared.user") || "User"}
        displayEmpty
      >
        <MenuItem value="">
          <em>{t("sharedAll") || "All users"}</em>
        </MenuItem>
        {users.map((user) => (
          <MenuItem key={user.id} value={user.id.toString()}>
            {getUserDisplayName(user)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

// Attributes renderer component
const AttributesRenderer = ({ attributes, type }) => {
  const t = useTranslation()

  if (!attributes || !type) return <span>-</span>

  switch (type) {
    case "sendCommand":
      return attributes.command ? <span>{t(prefixString("command", attributes.command))}</span> : <span>-</span>
    default:
      return <span>-</span>
  }
}

const CommandsReportPage = () => {
  const classes = useReportStyles()
  const t = useTranslation()
  const dispatch = useDispatch()

  // State
  const users = useSelector((state) => state?.users?.items ?? EMPTY_OBJECT, shallowEqual)
  const devices = useSelector((state) => state?.devices?.items ?? EMPTY_OBJECT, shallowEqual)
  const printing = useSelector((state) => state.session.printing ?? false)
  const usersMap = useSelector((state) => state?.usersMap ?? EMPTY_OBJECT, shallowEqual)

  const [columns, setColumns] = usePersistedState("commandColumns", ["userId", "eventTime", "deviceId", "attributes"])
  const [eventTypes, setEventTypes] = useState(["sendCommand"])
  const [items, setItems] = useState([])
  const [allItems, setAllItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [usersLoading, setUsersLoading] = useState(false)
  const [hasResults, setHasResults] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [userIdFilter, setUserIdFilter] = useState("")
  const [periodoFilter, setPeriodoFilter] = useState({ from: "", to: "" })
  const [usuarios, setUsuarios] = useState([])
  const [from, setFrom] = useState("")
  const [to, setTo] = useState("")

  // Load users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setUsersLoading(true)
        const response = await fetchOrThrow("/api/users?all=false&limit=500") // switched to fetchOrThrow
        const data = await response.json()

        const userNamesMap = {}
        const normalizedUsers = data.map((user) => {
          const displayName = user.name || user.email || `Usuário ${user.id}`
          userNamesMap[user.id] = displayName
          return { id: user.id, name: displayName }
        })

        dispatch({
          type: "usersMap/update",
          payload: userNamesMap,
        })

        setUsuarios(normalizedUsers)
      } catch (error) {
        console.error("Error fetching users:", error)
      } finally {
        setUsersLoading(false)
      }
    }

    fetchUsers()
  }, [dispatch])

  // Handle print event
  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false))
    }
    window.addEventListener("afterprint", handleAfterPrint)
    return () => {
      window.removeEventListener("afterprint", handleAfterPrint)
    }
  }, [dispatch])

  const fetchCommands = async (filters = {}) => {
    setHasResults(false)
    setLoading(true)
    try {
      const { period, ...queryFilters } = filters
      const query = new URLSearchParams()
      Object.entries(queryFilters).forEach(([key, value]) => {
        if (value) {
          if (Array.isArray(value)) {
            value.forEach((v) => query.append(key, v))
          } else {
            query.append(key, value)
          }
        }
      })

      eventTypes.forEach((type) => query.append("type", type))

      const endpoint = `/api/reports/commands?${query.toString()}`
      const response = await fetchOrThrow(endpoint) // switched to fetchOrThrow
      const contentType = response.headers.get("content-type")
      if (contentType === "application/json") {
        const data = await response.json()
        setAllItems(data)

        // preserved custom logic: aplica filtros locais e paginação já existentes.
        const filteredData = applyLocalFilters(
          data,
          queryFilters.userId ?? userIdFilter,
          period ?? periodoFilter,
        )
        setItems(filteredData)
        setHasResults(filteredData.length > 0)
        setPage(0)
      } else {
        window.location.assign(window.URL.createObjectURL(await response.blob()))
        setHasResults(true)
      }
    } catch (error) {
      console.error("Error fetching data:", error)
      dispatch(errorsActions.push(error.message))
      setHasResults(false)
    } finally {
      setLoading(false)
    }
  }

  const applyLocalFilters = (data, userId, period) => {
    let result = [...data]

    if (userId) {
      result = result.filter((item) => item.userId && item.userId.toString() === userId)
    }

    if (period.from || period.to) {
      result = result.filter((item) => {
        if (!item.eventTime) return true

        const itemDate = new Date(item.eventTime).getTime()

        if (period.from && period.to) {
          const fromDate = new Date(period.from).getTime()
          const toDate = new Date(period.to).setHours(23, 59, 59, 999)
          return itemDate >= fromDate && itemDate <= toDate
        } else if (period.from) {
          const fromDate = new Date(period.from).getTime()
          return itemDate >= fromDate
        } else if (period.to) {
          const toDate = new Date(period.to).setHours(23, 59, 59, 999)
          return itemDate <= toDate
        }

        return true
      })
    }

    return result
  }

  const filterByUserId = (userId) => {
    setUserIdFilter(userId)
    const filteredData = applyLocalFilters(allItems, userId, periodoFilter)
    setItems(filteredData)
    setHasResults(filteredData.length > 0)
    setPage(0)
  }

  const handleShow = useCatch(async ({ from, to }) => {
    if (!userIdFilter) {
      dispatch(errorsActions.push("Selecione ao menos um usuário para gerar o relatório."))
      setItems([])
      setAllItems([])
      setHasResults(false)
      return
    }

    const effectivePeriod = { from: from || "", to: to || "" }
    setPeriodoFilter(effectivePeriod)
    setFrom(effectivePeriod.from)
    setTo(effectivePeriod.to)
    setItems([])
    setAllItems([])
    setHasResults(false)

    const filters = {
      userId: userIdFilter,
    }
    if (effectivePeriod.from) {
      filters.from = effectivePeriod.from
    }
    if (effectivePeriod.to) {
      filters.to = effectivePeriod.to
    }
    filters.period = effectivePeriod

    await fetchCommands(filters)
  })

  const handleExportReport = ({ from: _fromParam, to: _toParam } = {}) => {
    if (!hasResults) {
      dispatch(errorsActions.push("Nenhum registro disponível para exportação."))
      return
    }
    // preserved custom logic – exporta a tabela renderizada atual.
    const table = document.querySelector("table")
    if (!table) {
      dispatch(errorsActions.push("Tabela não encontrada para exportação."))
      return
    }
    requestAnimationFrame(() => writeFileXLSX(utils.table_to_book(table), `${t("reportCommands")}.xlsx`))
  }

  const handlePrintReport = ({ from: fromParam, to: toParam } = {}) => {
    if (!hasResults) {
      dispatch(errorsActions.push("Nenhum registro disponível para impressão."))
      return
    }
    const start = fromParam || periodoFilter.from
    const end = toParam || periodoFilter.to
    if (start) {
      setFrom(start)
    }
    if (end) {
      setTo(end)
    }
    dispatch(sessionActions.setPrinting(true))
  }

  const handleSchedule = useCatch(async (userIds, groupIds, report) => {
    report.type = "commands"
    // Navigate to scheduled reports page if needed
  })

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const selectedUserName = userIdFilter ? usersMap[userIdFilter] ?? userIdFilter : undefined
  const selectedDeviceName = undefined

  useEffect(() => {
    if (printing) {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => nativePostMessage(`print|${t("reportCommands")}.pdf`)),
      )
    }
  }, [printing, t])

  const formatValue = (item, key) => {
    if (!item) return ""

    switch (key) {
      case "deviceId":
        return item[key] && devices[item[key]] ? devices[item[key]].name : ""
      case "userId":
        return item[key] && users[item[key]] ? users[item[key]].name : "-"
      case "eventTime":
        return item[key] ? formatTime(item[key]) : ""
      case "attributes":
        if (printing) {
          if (!item.attributes || !item.type) return "-"
          switch (item.type) {
            case "sendCommand":
              return item.attributes.command ? t(prefixString("command", item.attributes.command)) : "-"
            default:
              return "-"
          }
        }
        return <AttributesRenderer attributes={item.attributes} type={item.type} />
      default:
        return item[key] !== undefined ? item[key] : "-"
    }
  }

  if (printing) {
    return (
      <div>
        <PrintHeader
          title={t("reportCommands")}
          from={from}
          to={to}
          user={selectedUserName}
          device={selectedDeviceName}
        />
        <div>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>{t(columnsMap.get(key) || key)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id || Math.random()}>
                  {columns.map((column) => (
                    <TableCell key={`${item.id || Math.random()}-${column}`}>{formatValue(item, column)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={["reportTitle", "reportCommands"]}>
      <div className={classes.container}>
        <div className={classes.containerMain}>
          <div className={classes.header}>
            {/* standardized export/print/agenda menu */}
            <ReportFilter
              onShow={handleShow}
              onExport={handleExportReport}
              onPrint={handlePrintReport}
              onSchedule={handleSchedule}
              loading={loading}
              canRun={Boolean(userIdFilter)}
              hasResults={hasResults}
            >
              <Box className={classes.filterItem} sx={{ order: -1, minWidth: 200 }}>
                {usersLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <UserFilter onChange={filterByUserId} users={usuarios} loading={usersLoading} />
                )}
              </Box>
              <Box className={classes.filterItem} sx={{ order: 1, minWidth: 200 }}>
                <ColumnSelect
                  columns={columns}
                  setColumns={setColumns}
                  columnsArray={columnsArray}
                  rawValues={true}
                  disabled={loading}
                />
              </Box>
            </ReportFilter>
          </div>

          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>{t(columnsMap.get(key) || key)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading ? (
                items.length > 0 ? (
                  items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                    <TableRow key={item.id || Math.random()} hover>
                      {columns.map((key) => (
                        <TableCell key={`${item.id || Math.random()}-${key}`}>{formatValue(item, key)}</TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow style={{ height: 72 * 5 }}>
                    <TableCell align="center" colSpan={columns.length}>
                      {t("sharedNoData") || "No data available"}
                    </TableCell>
                  </TableRow>
                )
              ) : (
                <TableShimmer columns={columns.length} startAction={false} endAction={false} />
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={items.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t("sharedRowsPerPage") || "Rows per page:"}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("sharedOf") || "of"} ${count}`}
          />
        </div>
      </div>
    </PageLayout>
  )
}

export default CommandsReportPage
